import { FlexRow } from '@/components/common/Flex';
import { FlexProps } from '@chakra-ui/react';
import { AnimatePresence, Variants } from 'framer-motion';
import { useRef, useState } from 'react';
import { MotionBox } from './Motion';

type Direction = 'back' | 'forward';
export type NavigateFcn = (params: {
    indexOrId: string | number;
    direction: Direction;
}) => void;
export type GoBackFcn = (numOrId?: number | string) => void;
export type GoForwardFcn = (numOrId?: number | string) => void;
export type GoToFcn = (
    indexOrId: number | string,
    direction?: Direction
) => void;
export type ShowSlideupFcn = (frameId: string) => void;
export type HideSlideupFcn = () => void;

export type FrameRenderer = {
    id: string;
    render: (props: {
        navigate: NavigateFcn;
        goBack: GoBackFcn;
        goForward: GoForwardFcn;
        goTo: GoToFcn;
        showSlideup: ShowSlideupFcn;
        hideSlideup: HideSlideupFcn;
        isFirst?: boolean;
    }) => JSX.Element;
};

export interface SlideAnimatorProps extends FlexProps {
    frames: FrameRenderer[];
    initialFrameIndex?: number;
    slideupFrames?: FrameRenderer[];
}

export const SLIDE_DURATION_MS = 200;
const DURATION_S = SLIDE_DURATION_MS / 1000;
const DEFAULT_WIDTH = 500;
const DEFAULT_HEIGHT = 600;

const variants = {
    enter: ({ direction, width }: { direction: number; width: number }) => ({
        x: direction > 0 ? width : -width,
        opacity: 0,
    }),
    center: {
        zIndex: 1,
        x: 0,
        opacity: 1,
    },
    exit: ({ direction, width }: { direction: number; width: number }) => ({
        zIndex: 0,
        x: direction < 0 ? width : -width,
        opacity: 0,
    }),
} as Variants;
const slideupVariants = {
    enter: ({ height }: { height: number }) => ({
        opacity: 0,
        y: height,
    }),
    show: {
        zIndex: 2,
        opacity: 1,
        y: 0,
    },
    exit: ({ height }: { height: number }) => ({
        opacity: 0,
        y: height,
    }),
};

const SlideAnimator = ({
    frames,
    initialFrameIndex = 0,
    slideupFrames = [],
    ...props
}: SlideAnimatorProps) => {
    const ref = useRef<HTMLDivElement>(null);
    const [[frameIndex, direction], setFrame] = useState([
        initialFrameIndex,
        0,
    ]);
    const [slideupFrameId, setSlideupFrameId] = useState<string | null>(null);
    const showSlideup: ShowSlideupFcn = (frameId) => {
        setSlideupFrameId(frameId);
    };
    const hideSlideup = () => setSlideupFrameId(null);

    const navigate: NavigateFcn = ({ indexOrId, direction }) => {
        let newFrameIndex =
            typeof indexOrId === 'number'
                ? indexOrId
                : frames.findIndex((f) => f.id === indexOrId);

        // Prevent invalid frame index
        if (newFrameIndex < 0 || newFrameIndex >= frames.length) {
            newFrameIndex = frameIndex;
        }

        setFrame([newFrameIndex, direction === 'back' ? -1 : 1]);
    };

    const goBack: GoBackFcn = (numOrId = 1) => {
        if (typeof numOrId === 'string') {
            navigate({ indexOrId: numOrId, direction: 'back' });
        } else {
            const index = frameIndex - numOrId;
            navigate({ indexOrId: index, direction: 'back' });
        }
    };
    const goForward: GoForwardFcn = (numOrId = 1) => {
        if (typeof numOrId === 'string') {
            navigate({ indexOrId: numOrId, direction: 'forward' });
        } else {
            const index = frameIndex + numOrId;
            navigate({ indexOrId: index, direction: 'forward' });
        }
    };
    const goTo: GoToFcn = (indexOrId, direction = 'back') =>
        navigate({ indexOrId, direction });

    if (!frames.length) return null;

    const width = ref.current?.getBoundingClientRect().width ?? DEFAULT_WIDTH;
    const height =
        ref.current?.getBoundingClientRect().height ?? DEFAULT_HEIGHT;

    return (
        <FlexRow
            position="relative"
            w="full"
            h="full"
            overflow="hidden"
            ref={ref}
            {...props}
        >
            <AnimatePresence initial={false} custom={{ direction, width }}>
                <MotionBox
                    key={frameIndex}
                    position="absolute"
                    custom={{ direction, width }}
                    variants={variants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    // Known typing issue - no problem in operation.
                    // Details: https://chakra-ui.com/getting-started/with-framer#chakra-factory
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    transition={{
                        x: { type: 'spring', stiffness: 300, damping: 30 },
                        opacity: { duration: DURATION_S },
                    }}
                    w="full"
                    h="full"
                >
                    {frames[frameIndex].render({
                        navigate,
                        goBack,
                        goForward,
                        goTo,
                        showSlideup,
                        hideSlideup,
                        isFirst: frameIndex === 0,
                    })}
                </MotionBox>
                {slideupFrameId && (
                    <MotionBox
                        key={slideupFrameId}
                        position="absolute"
                        custom={{ height }}
                        variants={slideupVariants}
                        initial="enter"
                        animate={slideupFrameId ? 'show' : 'exit'}
                        exit="exit"
                        // Known typing issue - no problem in operation.
                        // Details: https://chakra-ui.com/getting-started/with-framer#chakra-factory
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        transition={{
                            y: { type: 'spring', stiffness: 300, damping: 30 },
                            opacity: { duration: DURATION_S },
                        }}
                        w="full"
                        h="full"
                    >
                        {slideupFrames
                            .find((f) => f.id === slideupFrameId)
                            ?.render({
                                navigate,
                                goBack,
                                goForward,
                                goTo,
                                showSlideup,
                                hideSlideup,
                            })}
                    </MotionBox>
                )}
            </AnimatePresence>
        </FlexRow>
    );
};

export default SlideAnimator;
